import Downarrow from '@/src/assets/svgs/downarrow'
import { Shimmer, Typography } from '@/src/ct-design/lib'
import {
    addMonths,
    addYears,
    eachDayOfInterval,
    endOfMonth,
    format,
    isAfter,
    isBefore,
    isSameDay,
    isSameMonth,
    isToday,
    startOfMonth,
    subMonths,
    subYears
} from 'date-fns'
import React, { useEffect, useState } from 'react'
import useTheme from '../../hooks/useTheme'
import { weekdays } from './config'
import DropdownContainer from './DropdownContainer/DropdownContainer'
import StyledCalendarGridContainer, {
    StyledCalendarGrid,
    StyledDayCell,
    StyledWeekdayHeader
} from './StyledCalendarGrid/StyledCalendarGrid'
import StyledCalendarHeader, {
    StyledMonthNavButton
} from './StyledCalendarHeader/StyledCalendarHeader'
import StyledCalendarPopup from './StyledCalendarPopup/StyledCalendarPopup'
import StyledCalendarWrapper from './StyledCalendarWrapper/StyledCalendarWrapper'
import StyledDateDropdown from './StyledDateDropdown/StyledDateDropdown'
import StyledDropdownArrow from './StyledDropdownArrow/StyledDropdownArrow'
import StyledDropdownItem from './StyledDropdownItem/StyledDropdownItem'
import StyledDropdownList from './StyledDropdownList/StyledDropdownList'
import StyledHeaderText from './StyledHeaderText/StyledHeaderText'
import { SimpleCalendarProps } from './type'

const Calendar: React.FC<SimpleCalendarProps> = ({
    initialDate,
    onDateChange,
    type,
    isLoading,
    selectedDate,
    error,
    startYear = new Date().getFullYear(),
    endYear = new Date().getFullYear() + 10,
    yearOrderDesc = false,
    disableDate = null,
    minDate = null,
    maxDate = null,
    weekdayEnable = false
}) => {
    const yearDropdownRef = React.useRef<HTMLDivElement>(null)
    const [isOpen, setIsOpen] = useState(false)
    const [viewDate, setViewDate] = useState(
        selectedDate || initialDate || new Date()
    )
    const [showMonthDropdown, setShowMonthDropdown] = useState(false)
    const [showYearDropdown, setShowYearDropdown] = useState(false)
    const theme = useTheme()

    // Scroll to bottom when yearOrderDesc is true and dropdown is opened
    useEffect(() => {
        if (showYearDropdown && yearOrderDesc && yearDropdownRef.current) {
            yearDropdownRef.current.scrollTop =
                yearDropdownRef.current.scrollHeight
        }
    }, [showYearDropdown, yearOrderDesc])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                isOpen &&
                !(event.target as Element).closest(
                    `.${StyledCalendarWrapper.styledComponentId}`
                )
            ) {
                setIsOpen(false)
                setShowMonthDropdown(false)
                setShowYearDropdown(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    useEffect(() => {
        setViewDate(selectedDate || initialDate || new Date())
    }, [selectedDate])

    const toggleCalendar = () => {
        setIsOpen(!isOpen)
        setShowMonthDropdown(false)
        setShowYearDropdown(false)
    }

    const isDateDisabled = (date: Date) => {
        // Check if date is within min/max range if provided
        if (minDate && isBefore(date, minDate)) return true
        if (maxDate && isAfter(date, maxDate)) return true
        
        // Legacy support for disableDate (convert string to Date if needed)
        if (!disableDate) return false
        
        const disableDateObj = typeof disableDate === 'string' ? new Date(disableDate) : disableDate

        if (!yearOrderDesc) {
            return isBefore(date, disableDateObj)
        } else {
            return isAfter(date, disableDateObj)
        }
    }

    const handleDateSelect = (date: Date) => {
        if (isDateDisabled(date)) return

        setIsOpen(false)
        setShowMonthDropdown(false)
        setShowYearDropdown(false)

        if (onDateChange) {
            onDateChange(type, date)
        }
    }

    const nextMonth = () => {
        setViewDate(addMonths(viewDate, 1))
    }

    const prevMonth = () => {
        setViewDate(subMonths(viewDate, 1))
    }

    const nextYear = () => {
        setViewDate(addYears(viewDate, 1))
    }

    const prevYear = () => {
        setViewDate(subYears(viewDate, 1))
    }

    const toggleMonthDropdown = (e: React.MouseEvent) => {
        e.stopPropagation()
        setShowYearDropdown(false)
        setShowMonthDropdown(!showMonthDropdown)
    }

    const toggleYearDropdown = (e: React.MouseEvent) => {
        e.stopPropagation()
        setShowMonthDropdown(false)
        setShowYearDropdown(!showYearDropdown)
    }

    const handleMonthSelect = (monthIndex: number) => {
        const newDate = new Date(viewDate)
        newDate.setMonth(monthIndex)
        setViewDate(newDate)
        setShowMonthDropdown(false)
    }

    const handleYearSelect = (year: number) => {
        const newDate = new Date(viewDate)
        newDate.setFullYear(year)
        setViewDate(newDate)
        setShowYearDropdown(false)
    }

    // Generate array of all months
    const months = [
        { value: 'January', key: 0 },
        { value: 'February', key: 1 },
        { value: 'March', key: 2 },
        { value: 'April', key: 3 },
        { value: 'May', key: 4 },
        { value: 'June', key: 5 },
        { value: 'July', key: 6 },
        { value: 'August', key: 7 },
        { value: 'September', key: 8 },
        { value: 'October', key: 9 },
        { value: 'November', key: 10 },
        { value: 'December', key: 11 }
    ]
    const generateYearRange = () => {
        const years: number[] = []
        const start = Math.min(startYear ?? 2020, endYear ?? 2030)
        const end = Math.max(startYear ?? 2020, endYear ?? 2030)

        // Always push from oldest to newest
        for (let year = start; year <= end; year++) {
            years.push(year)
        }

        return years
    }

    const years = generateYearRange()

    const renderCalendarDays = (month: Date) => {
        const monthStart = startOfMonth(month)
        const monthEnd = endOfMonth(month)
        const days = eachDayOfInterval({ start: monthStart, end: monthEnd })

        const startDay = monthStart.getDay()
        const blanks = Array(startDay).fill(null)

        return (
            <>
                {blanks.map((_, index) => (
                    <StyledDayCell key={`blank-${index}`} />
                ))}

                {days.map((day) => {
                    const isSelected = selectedDate
                        ? isSameDay(day, selectedDate)
                        : false
                    const disabled = isDateDisabled(day)
                    return (
                        <StyledDayCell
                            key={day.toString()}
                            isToday={isToday(day)}
                            isSelected={isSelected}
                            isCurrentMonth={isSameMonth(day, month)}
                            isDisabled={disabled}
                            onClick={() => !disabled && handleDateSelect(day)}
                        >
                            <Typography
                                variant='P2'
                                color={
                                    isSelected
                                        ? 'neutral'
                                        : disabled
                                          ? 'disabled'
                                          : 'primary'
                                }
                            >
                                {format(day, 'd')}
                            </Typography>
                        </StyledDayCell>
                    )
                })}
            </>
        )
    }

    const renderMonthDropdown = () => {
        return (
            <StyledDropdownList>
                {months.map((month) => (
                    <StyledDropdownItem
                        key={month.key}
                        isSelected={month.key === viewDate.getMonth()}
                        onClick={() => handleMonthSelect(month.key)}
                    >
                        <Typography variant='P2'>{month.value}</Typography>
                    </StyledDropdownItem>
                ))}
            </StyledDropdownList>
        )
    }

    const renderYearDropdown = () => {
        return (
            <StyledDropdownList ref={yearDropdownRef}>
                {years.map((year) => (
                    <StyledDropdownItem
                        key={year}
                        isSelected={year === viewDate.getFullYear()}
                        onClick={() => handleYearSelect(year)}
                    >
                        <Typography variant='P2'>{year}</Typography>
                    </StyledDropdownItem>
                ))}
            </StyledDropdownList>
        )
    }

    if (isLoading) {
        return (
            <Shimmer
                borderRadius={theme?.border?.radius?.[6] || '6px'}
                height={'40px'}
                width='100%'
            />
        )
    }

    return (
        <StyledCalendarWrapper>
            <StyledDateDropdown
                isOpen={isOpen}
                onClick={toggleCalendar}
                error={!!error}
            >
                <Typography variant='P2'>
                    {selectedDate
                        ? weekdayEnable
                            ? format(selectedDate, 'EEE, MMM d, yyyy')
                            : format(selectedDate, 'MMM d, yyyy')
                        : 'Select date'}
                </Typography>
                <StyledDropdownArrow
                    isOpen={isOpen}
                    style={{ width: '20', height: '20' }}
                />
            </StyledDateDropdown>

            {isOpen && (
                <StyledCalendarPopup>
                    <StyledCalendarHeader>
                        <DropdownContainer>
                            <StyledMonthNavButton onClick={prevMonth}>
                                <Downarrow
                                    style={{
                                        transform: 'rotate(90deg)',
                                        transformOrigin: 'center',
                                        width: '16',
                                        height: '16'
                                    }}
                                />
                            </StyledMonthNavButton>
                            <StyledHeaderText onClick={toggleMonthDropdown}>
                                <Typography variant='B2' className='ta-c'>
                                    {format(viewDate, 'MMM')}
                                </Typography>
                                <Downarrow
                                    style={{
                                        width: '16',
                                        height: '16',
                                        marginLeft: '4px'
                                    }}
                                />
                            </StyledHeaderText>
                            <StyledMonthNavButton onClick={nextMonth}>
                                <Downarrow
                                    style={{
                                        transform: 'rotate(-90deg)',
                                        transformOrigin: 'center',
                                        width: '15',
                                        height: '20'
                                    }}
                                />
                            </StyledMonthNavButton>
                        </DropdownContainer>
                        <DropdownContainer>
                            <StyledMonthNavButton onClick={prevYear}>
                                <Downarrow
                                    style={{
                                        transform: 'rotate(90deg)',
                                        transformOrigin: 'center',
                                        width: '16',
                                        height: '16'
                                    }}
                                />
                            </StyledMonthNavButton>
                            <StyledHeaderText onClick={toggleYearDropdown}>
                                <Typography variant='B2' className='ta-c'>
                                    {format(viewDate, 'yyyy')}
                                </Typography>
                                <Downarrow
                                    style={{
                                        width: '16',
                                        height: '16',
                                        marginLeft: '4px'
                                    }}
                                />
                            </StyledHeaderText>
                            <StyledMonthNavButton onClick={nextYear}>
                                <Downarrow
                                    style={{
                                        transform: 'rotate(-90deg)',
                                        transformOrigin: 'center',
                                        width: '16',
                                        height: '16'
                                    }}
                                />
                            </StyledMonthNavButton>
                        </DropdownContainer>
                    </StyledCalendarHeader>

                    {showMonthDropdown ? (
                        renderMonthDropdown()
                    ) : showYearDropdown ? (
                        renderYearDropdown()
                    ) : (
                        <StyledCalendarGridContainer>
                            <StyledCalendarGrid>
                                {weekdays.map((day) => (
                                    <StyledWeekdayHeader key={day.key}>
                                        <Typography
                                            color='disabled'
                                            variant='P2'
                                        >
                                            {day.value}
                                        </Typography>
                                    </StyledWeekdayHeader>
                                ))}

                                {renderCalendarDays(viewDate)}
                            </StyledCalendarGrid>
                        </StyledCalendarGridContainer>
                    )}
                </StyledCalendarPopup>
            )}
            {error && (
                <Typography variant='P3' color='warning' className='ml-4 mt-2'>
                    {error}
                </Typography>
            )}
        </StyledCalendarWrapper>
    )
}

Calendar.defaultProps = {
    onDateChange: () => {}
}

export default Calendar
